import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { PartnerCategory } from '../../../common/models'
import {
  createPartnerCategory,
  deletePartnerCategory,
  getPartnerCategories,
  updatePartnerCategory,
} from '../../../core/services/partners'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import FormGroup from '@/core/components/FormGroup'
import { Icon } from '@/common/components/Icon'

export interface IPartnerCategoryValues {
  name?: string
  weight?: number
  published?: boolean
  publishedAt?: number
}

export interface CreatePartnerCategoryModalProps {
  partnerCategory?: PartnerCategory
  setShowCreateModal: (showEdit: boolean) => void
  setPartnerCategories: (PartnerCategory: PartnerCategory[]) => void
  showCreateModal: boolean
  setEditMode: (editMode: boolean) => void
  editMode: boolean
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
}

export const CreatePartnerCategoryModal = (
  props: CreatePartnerCategoryModalProps,
): JSX.Element => {
  const {
    partnerCategory,
    setShowCreateModal,
    setPartnerCategories,
    showCreateModal,
    setEditMode,
    editMode,
    page,
    size,
    setTotalElemenets,
  } = props

  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    if (editMode) {
      setValue('name', partnerCategory?.name)
      setValue('published', partnerCategory?.published)
      setValue('weight', partnerCategory?.weight)
      setValue('publishedAt', partnerCategory?.publishedAt)
    }
  }, [partnerCategory, editMode])

  const schema = yup.object().shape({
    name: yup.string(),
    published: yup.boolean(),
    weight: yup.number(),
    publishedAt: yup.number(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    setValue,
    control,
  } = useForm<IPartnerCategoryValues>({
    mode: 'onChange',
    resolver: yupResolver<IPartnerCategoryValues>(schema),
    defaultValues: {
      name: '',
      weight: 0,
      published: false,
      publishedAt: new Date().valueOf(),
    },
  })

  const handleDelete = () => {
    setDeleteConfirmationModal(true)
  }

  const deletePartnerCategoryFromModal = async () => {
    try {
      if (partnerCategory) {
        const { status } = await deletePartnerCategory([partnerCategory.id])
        const response = await getPartnerCategories({
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        setPartnerCategories(response.data.content)
        if (status === 204 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  const handleClose = () => {
    setShowCreateModal(false)
    setEditMode(false)
    setDeleteConfirmationModal(false)
    reset()
  }

  const submit = async (values: IPartnerCategoryValues) => {
    try {
      const data: IPartnerCategoryValues = {
        name: values.name,
        weight: values.weight,
        published: values.published,
        publishedAt: new Date().valueOf(),
      }
      if (partnerCategory) {
        const { status } = await updatePartnerCategory(partnerCategory.id, data)
        const response = await getPartnerCategories({
          page: page - 1,
          size,
        })
        setPartnerCategories(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createPartnerCategory(data)
        const response = await getPartnerCategories({
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        setPartnerCategories(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      handleClose()
    }
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Partner Categories"
              subtitle={
                editMode ? 'Edit Partner Category' : 'Add New Partner Category'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {editMode && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="partner-category-form"
              type="submit"
              variant="primary"
              disabled={isSubmitting || Object.keys(dirtyFields).length === 0}
              loading={isSubmitting}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="partner-category-form"
          className="space-y-7"
          onSubmit={handleSubmit(submit)}
        >
          <FormGroup label="Name">
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          <FormGroup label="Weight">
            <Input
              isInvalid={!!errors?.weight?.message}
              {...register('weight')}
              type="number"
              placeholder="Enter Weight"
              min={0}
              max={100}
            />
          </FormGroup>

          <Controller
            name="published"
            control={control}
            render={({ field }) => (
              <FormGroup
                label="Published"
                direction="row"
                labelWrapperClassName="me-4"
              >
                <Checkbox
                  className="border-monochrome-1"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) => {
                    field.onChange(Boolean(checked))
                  }}
                />
              </FormGroup>
            )}
          />
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Partner Category'}
        deleteFunc={deletePartnerCategoryFromModal}
        deletedItem={partnerCategory?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
